// Line icons in the spirit of SF Symbols: 24 px grid, 1.8 px stroke, round caps.
const paths = {
  compose: 'M4 20h4l10.5-10.5a2.1 2.1 0 0 0-3-3L5 17v3zM14 7.5l3 3',
  reply: 'M10 7 4 12l6 5M4 12h10a6 6 0 0 1 6 6',
  forward: 'M14 7l6 5-6 5M20 12H10a6 6 0 0 0-6 6',
  done: 'M4 7h16M5 7v11a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7M9 11h6M3 4h18v3H3z',
  snooze: 'M12 21a8 8 0 1 0 0-16 8 8 0 0 0 0 16zM12 9v4l2.5 2M5 3 2 6M19 3l3 3',
  teach: 'M12 3l1.8 4.7L18.5 9.5l-4.7 1.8L12 16l-1.8-4.7L5.5 9.5l4.7-1.8zM18 15l.8 2.2L21 18l-2.2.8L18 21l-.8-2.2L15 18l2.2-.8z',
  back: 'M15 5l-7 7 7 7',
  up: 'M6 15l6-6 6 6',
  down: 'M6 9l6 6 6-6',
  gear: 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z',
  send: 'M12 19V5M5 12l7-7 7 7',
} as const

export type GlyphName = keyof typeof paths

export function Glyph({ name, size = 22 }: { name: GlyphName; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={paths[name]} />
    </svg>
  )
}

/** Press a shortcut from a touch control, so phones reuse the keyboard actions exactly. */
export function press(key: string, options: { shiftKey?: boolean; metaKey?: boolean } = {}) {
  window.dispatchEvent(new KeyboardEvent('keydown', { key, bubbles: true, ...options }))
}

const MODIFIERS = new Set(['⌘', '⇧', '⌥', '⌃'])
/** "⇧R" → ⇧ R, "j k" → j k, "g t" → g t, "esc" → esc: each key gets its own keycap. */
export function keyCaps(combo: string): string[] {
  const caps: string[] = []
  for (const part of combo.trim().split(/\s+/)) {
    let rest = part
    while (rest.length > 1 && MODIFIERS.has(rest[0]!)) { caps.push(rest[0]!); rest = rest.slice(1) }
    if (rest) caps.push(rest)
  }
  return caps
}

/** Keycaps, Raycast-manual style: one small bordered cap per key. */
export function Kbd({ k, className = '' }: { k: string | number; className?: string }) {
  return <span className={`keys ${className}`}>{keyCaps(String(k)).map((cap, i) => <kbd key={i} className={/^[⌘⇧⌥⌃↵←→↑↓]$/.test(cap) ? 'sym' : cap.length > 1 ? 'wide' : undefined}>{cap}</kbd>)}</span>
}
